import React from 'react';
import { Button, Typography, Switch, FormControlLabel, List, ListItem } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import clsx from 'clsx';
import { FIELD_TYPES } from '../Interfaces.config';
import { isFieldVisible } from '../Interfaces.shared';

const toChecked = (value, fallback = true) => {
  if (value === undefined || value === null) return fallback;
  return value === 1 || value === true;
};

// Node title in accordion header
const NodeCard = ({ node, config, onEdit, onDelete, onToggleFieldChange, t, classes }) => {
  const headerToggles = config?.headerToggles || [];
  const typeLabel = config?.showTypeInTitle && config?.typeLabel ? ` (${config.typeLabel})` : '';

  return (
    <div className={classes.listHeaderContainer} style={{ width: '100%', alignItems: 'center' }}>
      <Typography className={classes.listHeaderNameSummary} variant='subtitle1'>
        {node.name}
        {typeLabel}
      </Typography>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={(e) => e.stopPropagation()}>
        {headerToggles.map((fieldKey) => {
          const field = config.fields.find((f) => f.key === fieldKey);
          if (!field || !onToggleFieldChange) return null;

          return (
            <FormControlLabel
              key={fieldKey}
              control={
                <Switch
                  checked={toChecked(node[fieldKey])}
                  onChange={(e) => onToggleFieldChange(fieldKey, e.target.checked)}
                  color='primary'
                  size='small'
                />
              }
              label={t(field.labelKey)}
              style={{ marginLeft: '8px' }}
            />
          );
        })}

        {onDelete && (
          <Button
            size='small'
            variant='outlined'
            onClick={onDelete}
            color='primary'
            startIcon={
              <DeleteIcon
                fontSize='small'
                className={classes.icon_button}
                style={{ color: '#bdbdbd', height: '14px', width: '14px' }}
              />
            }
            className={clsx(classes.buttonGeneral, classes.deleteButton)}
          >
            {t('Settings.delete')}
          </Button>
        )}

        {onEdit && (
          <Button
            size='small'
            variant='contained'
            onClick={onEdit}
            color='primary'
            startIcon={<EditIcon fontSize='small' className={classes.icon_button} />}
            className={clsx(classes.buttonGeneral, classes.editButton)}
          >
            {t('Settings.edit')}
          </Button>
        )}
      </div>
    </div>
  );
};

export const formatDisplayValue = (node, field, t, fallbacks = {}) => {
  const displayValue = node[field.key];

  if (field.display === 'activeState' || field.type === FIELD_TYPES.BOOLEAN) {
    return toChecked(displayValue, false) ? t('Settings.active') : t('Settings.inactive');
  }

  if (field.display === 'masked') {
    return displayValue ? '••••••••' : '—';
  }

  if (field.type === FIELD_TYPES.SELECT) {
    const option = field.options?.find((o) => String(o.value) === String(displayValue));
    return option ? t(option.labelKey) : displayValue;
  }

  if (displayValue === 0) return 0;
  if (displayValue) return displayValue;
  if (field.placeholderFrom && fallbacks[field.placeholderFrom]) {
    return fallbacks[field.placeholderFrom];
  }
  return '—';
};

// Node information in accordion details
NodeCard.Details = ({ node, config, t, classes }) => {
  const hiddenFields = new Set([...(config.headerToggles || []), ...(config.hiddenDetailFields || [])]);

  return (
    <div className={classes.nodeDetailsContainer}>
      <div className={classes.nodeDetailsItems}>
        {(config.displayFields || [])
          .filter((fieldKey) => !hiddenFields.has(fieldKey))
          .map((fieldKey) => {
            const field = config.fields.find((f) => f.key === fieldKey);
            if (!field || !isFieldVisible(field, node)) return null;

            if (field.display === 'institutions') {
              const institutions = node[fieldKey];
              return (
                <div key={fieldKey} className={classes.nodeDetailsItem} style={{ gridColumn: '1 / -1' }}>
                  <Typography className={classes.nodeTitles} style={{ width: '150px' }}>
                    {t(field.labelKey)}
                  </Typography>
                  <List className={classes.listContainer} style={{ paddingLeft: '4px' }}>
                    {Array.isArray(institutions) && institutions.length > 0 ? (
                      institutions.map((institution, idx) => (
                        <ListItem
                          key={`${node.name}-${node._typeId || config.id}-institution-${idx}`}
                          style={{ padding: 0, width: '100%' }}
                          className={classes.nodeValues}
                        >
                          {`- ${institution}`}
                        </ListItem>
                      ))
                    ) : (
                      <ListItem style={{ padding: 0, width: '100%' }}>
                        <Typography className={classes.nodeValues}>{t('ResultView.all')}</Typography>
                      </ListItem>
                    )}
                  </List>
                </div>
              );
            }

            return (
              <div key={fieldKey} className={classes.nodeDetailsItem}>
                <Typography className={classes.nodeTitles} style={{ width: '150px' }}>
                  {t(field.labelKey)}
                </Typography>
                <Typography className={classes.nodeValues}>{formatDisplayValue(node, field, t)}</Typography>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default NodeCard;
